import { CircleAlert, CircleCheck, Trash2 } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import useSWR from "swr";
import Skeleton from "../../components/Skeleton";
import { tableHeaders } from "./OrderItems";
import { useLocation } from "react-router-dom";
import { Modal } from "flowbite-react";
import { Tooltip } from "flowbite-react";
import { useUserServices } from "../../context/DealerProfile";

const fetchProductImage = async (productId) => {
  try {
    const response = await fetch(
      `/server/dealer_backend/fetch-item-image?productId=${productId}`
    );
    const imageObjectUrl = await response.json();
    return imageObjectUrl;
  } catch (error) {
    console.error("Error fetching product image:", error);
    return null;
  }
};

const OrderItemRow = ({
  item,
  handleItemChange,
  removeItem,
  index,
  onlyInfo = false,
  stage,
  formData,
  isReturn,
}) => {
  const {
    data: imageSrc,
    isLoading,
    error,
  } = useSWR(
    item?.id
      ? `/fetch-item-image/${onlyInfo ? item.Product_Name?.id : item.id}`
      : null,
    () => fetchProductImage(onlyInfo ? item.Product_Name?.id : item.id),
    {
      revalidateOnFocus: false, // Prevents re-fetching on tab/window focus
      dedupingInterval: 60 * 60 * 1000, // Cache the response for 1 hour
    }
  );

  const {
    dealerDetail: { parentAccount },
    priceItem,
  } = useUserServices();
  const location = useLocation();
  // filter out url where we need to showcase the requested quantity
  const isForDefaultQyuantity = location.pathname.startsWith("/order-history/");
  const [openModal, setOpenModal] = useState(false);

  const imageSrcBase = imageSrc?.imageDetail?.details?.output;

  // Model Hide
  const modalRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setOpenModal(false);
      }
    }
    if (openModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openModal]);

  const isAllowedStage = ![
    "Void",
    "Pending For Availability Check",
    "Pending for Approval - CEO",
    "Pending for Approval - Country Manager",
    "Pending for Approval",
  ].includes(formData?.Stage);

  return (
    <>
      <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
        {tableHeaders.map((header, headerIndex) => {
          let fieldValue;
          if (onlyInfo)
            fieldValue =
              item[header.onlyInfoKey]?.name ?? item[header.onlyInfoKey];
          else fieldValue = item[header.apiName]?.name ?? item[header.apiName];

          if (
            (header.apiName === "Requested_Quantity" &&
              !isForDefaultQyuantity) ||
            (header.onlyInfo && !onlyInfo) ||
            (onlyInfo && !header?.onlyInfoKey) ||
            (header.onlyInfoKey === "Stock_Availble" &&
              stage !== "Confirm" &&
              onlyInfo)
          )
            return null;

          let cellContent;

          // Check if the header is for product images
          if (header.apiName === "Record_Image") {
            cellContent = isLoading ? (
              <Skeleton />
            ) : error ? (
              <p>Error loading image!</p>
            ) : (
              <img
                className="w-14 aspect-square rounded-full cursor-pointer object-contain"
                src={
                  imageSrcBase
                    ? `data:image/png;base64,${imageSrcBase}`
                    : "/app/favicon.png"
                }
                alt="Product"
                // onClick={handleImageClick}
                onClick={() => setOpenModal(true)}
              />
            );

            // Display stock availability with stage and onlyInfo condition
          } else if (
            header.apiName === "Stock_Available" &&
            stage === "Confirm" &&
            onlyInfo
          ) {
            const tooltipMap = {
              Yes: {
                content: "Stock Available",
                icon: <CircleCheck className="text-green-600 cursor-pointer" />,
              },
              No: {
                content: "Stock Not Available",
                icon: (
                  <CircleAlert className="text-yellow-500 cursor-pointer" />
                ),
              },
              "Yes - Bonded": {
                content: "Stock Bonded Available",
                icon: <CircleAlert className="text-green-600 cursor-pointer" />,
              },
              "Yes - Non Bonded": {
                content: "Stock Non Bonded Available",
                icon: <CircleAlert className="text-green-600 cursor-pointer" />,
              },
              "No - Bonded": {
                content: "Stock No Bonded Available",
                icon: (
                  <CircleAlert className="text-yellow-500 cursor-pointer" />
                ),
              },
              "No - Non Bonded": {
                content: "Stock No Non-Bonded Available",
                icon: (
                  <CircleAlert className="text-yellow-500 cursor-pointer" />
                ),
              },

              default: {
                content: "No Both Available",
                icon: (
                  <CircleAlert className="text-yellow-500 cursor-pointer" />
                ),
              },
            };

            const { content, icon } =
              tooltipMap[fieldValue] || tooltipMap["default"];

            cellContent = (
              <Tooltip content={content} placement="left">
                {icon}
              </Tooltip>
            );
            // Editable quantity field
          } else if (header.apiName === "Quantity" && !onlyInfo) {
            cellContent = (
              <input
                type="number"
                value={fieldValue}
                onChange={(e) =>
                  handleItemChange(
                    index,
                    "Quantity",
                    parseFloat(e.target.value)
                  )
                }
                className="w-24 rounded-md text-right"
                max={item?.maxQuantity ? item.maxQuantity : 1000}
                min={item?.Minimum_Order_level && !isReturn ? 1 : 1}
              />
            );

            // Actions for Remarks item
          } else if (header.apiName === "MOQ_Remarks" && !onlyInfo) {
            cellContent = (
              <textarea
                type="number"
                className={`w-24 rounded-md text-right ${
                  item?.Minimum_Order_level > item?.Quantity
                    ? "border border-red-500"
                    : ""
                }`}
                value={fieldValue}
                onChange={(e) =>
                  handleItemChange(index, "Remarks", e.target.value)
                }
              />
            );

            // Actions for removing item
          } else if (header.apiName === "actions" && !onlyInfo) {
            cellContent = (
              <button
                type="button"
                className="text-red-500"
                onClick={() => removeItem(index)}
              >
                <Trash2 />
              </button>
            );

            // Calculating total value
          } else if (header.apiName === "Distributors_Discount_Price") {
            // Calculate discounted price
            // let discountedPrice =
            //   (item?.Channel_Discounted_Price ?? 0) -
            //   ((item?.Channel_Discounted_Price ?? 0) * (item?.Discount ?? 0)) /
            //     100;

            // // Ensure the price is non-negative and apply correct condition
            // cellContent = isAllowedStage
            //   ? Math.max(discountedPrice, 0).toFixed(2) // If stage is allowed, apply discount
            //   : (item?.Channel_Discounted_Price ?? 0).toFixed(2); // Otherwise, show original price

            let basePrice =
              item?.Channel_Discounted_Price ??
              item?.Distributors_Discount_Price ??
              // item?.Unit_Price -
              //   (item?.Unit_Price * parentAccount?.Price_Tier2) / 100 ??
              0;

            // Calculate discounted price
            let discountedPrice =
              basePrice - (basePrice * (item?.Discount ?? 0)) / 100;

            // Ensure the price is non-negative and apply correct condition
            cellContent = isAllowedStage
              ? Math.max(discountedPrice, 0).toFixed(2) // If stage is allowed, apply discount
              : basePrice.toFixed(2); // Otherwise, show original price

            // Default content
          } else if (header.apiName === "Total_Value") {
            // let total_value =
            //   (item?.Channel_Discounted_Price ?? 0) * (item?.Quantity ?? 0);
            let total_value =
              item?.Discount > 0 && isAllowedStage
                ? (item?.Total_Value ?? 0).toFixed(2) // If stage is allowed, apply discount
                : (
                    item?.Total_Distributor_Price ??
                    item?.Total_Value ??
                    0
                  ).toFixed(2);

            // console.log(total_value);
            cellContent = total_value;
            // Default content
          } else {
            cellContent = fieldValue;
          }

          return (
            <td
              key={headerIndex}
              className={`px-6 py-4 text-right ${
                ["Description"].includes(header.apiName) ? "text-center" : ""
              }`}
            >
              {cellContent}
            </td>
          );
        })}
      </tr>
      <Modal
        show={openModal}
        onClose={() => setOpenModal(false)}
        ref={modalRef}
        size="md"
      >
        <Modal.Body>
          <img
            className="object-contain w-[400px]"
            src={
              imageSrcBase
                ? `data:image/png;base64,${imageSrcBase}`
                : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRvT1G0T-1M2KBjEGJYsYxd5qA4j9vyejFL1ArtUQVBTxPVkPzoNS4g-K0M4HiOLwlzYPs&usqp=CAU"
            }
            alt="Product"
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default OrderItemRow;
